import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Card, CircularProgress, Divider, Typography, alpha, useTheme } from '@mui/material';
import type { ReactNode } from 'react';
import ModalHelpClub from '~/modals/ModalHelpClub';
import { JOURNEY_STICKY_TOP_VAR } from '~/components/StandardJourney/journeyStickyTop';
import { radius } from '~/styles/tokens/shape';

export type JourneyQuoteSubscriptionRow = {
  id: string;
  label: string;
  amount: number;
  /** Descrição opcional do item (ex.: clube) exibida via ícone de ajuda ao lado do rótulo. */
  description?: string;
};

export type JourneyQuoteSummaryColumnProps = {
  headline?: ReactNode;
  subscriptionRows: JourneyQuoteSubscriptionRow[];
  serviceChargeFormatted: string | null;
  showOperationalRow: boolean;
  totalMonthlyNumber: number;
  membershipFee: number;
  productsLoading: boolean;
  advancing: boolean;
  disableAdvance: boolean;
  advanceLabel?: string;
  onAdvance: () => void;
  /** Quando falso, oculta o botão principal (ex.: página de pagamento, só resumo). */
  showAdvanceButton?: boolean;
  /** Quando definido, mostra «Baixar cotação (PDF)» abaixo do botão principal. */
  onDownloadPdf?: () => void;
  /** Marca o cartão da taxa única como paga (selo verde do Figma 1680:5369). */
  membershipPaid?: boolean;
  /** Checkboxes de termos — no Figma 1680:5063 vivem aqui, acima do botão principal. */
  termsSlot?: ReactNode;
  /** Texto do aviso de segurança. Padrão: contratação; a renovação passa o próprio texto. */
  securityMessage?: string;
};

/** Valor hero («R$ X») reduzido na coluna estreita (md) para «R$ X /mês» + «por 12 meses» caberem numa linha. */
const SUMMARY_HERO_FONT_SIZE = { lg: '1.375rem', md: '1.25rem', sm: '1.5rem', xs: '1.25rem' };
const SUMMARY_SUFFIX_FONT_SIZE = { lg: '1rem', md: '0.875rem', sm: '1rem', xs: '0.875rem' };
const SUMMARY_BADGE_FONT_SIZE = { lg: '1rem', xs: '0.875rem' };

const fmt = (value: number) =>
  new Intl.NumberFormat('pt-BR', { currency: 'BRL', minimumFractionDigits: 2, style: 'currency' }).format(value);

/** Linhas de cobertura/clube: valor zero é exibido como "Grátis" em vez de "R$ 0,00". */
const fmtPrice = (value: number) => (value === 0 ? 'Grátis' : fmt(value));

export default function JourneyQuoteSummaryColumn({
  headline,
  subscriptionRows,
  serviceChargeFormatted,
  showOperationalRow,
  totalMonthlyNumber,
  membershipFee,
  productsLoading,
  advancing,
  disableAdvance,
  advanceLabel = 'Avançar para pagamento',
  onDownloadPdf,
  membershipPaid = false,
  termsSlot,
  onAdvance,
  showAdvanceButton = true,
  securityMessage = 'Seus dados estão protegidos e serão usados apenas para a contratação.'
}: JourneyQuoteSummaryColumnProps) {
  const theme = useTheme();
  const membershipFeeFormatted = fmt(membershipFee);
  const totalMonthlyFormatted = fmt(totalMonthlyNumber);

  const cardSx = {
    borderRadius: `${radius.medium}px`,
    boxShadow: `0px 4px 2px ${theme.customColors.elevation}`,
    gap: `${theme.spacing(2)}`,
    p: `${theme.spacing(3)}`,
    width: '100%'
  } as const;

  const row = (label: ReactNode, valueElement: ReactNode, key: string) => (
    <Box
      display="flex"
      gap={1}
      justifyContent="space-between"
      key={key}
      pb={1}
      pt={1}
      sx={{
        '&:first-of-type': {
          pb: `${theme.spacing(1)}`,
          pt: 0
        }
      }}
      width="100%"
    >
      <Typography sx={{ flex: '1 1 auto', fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }} variant="subtitle2">
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexShrink: 0,
          justifyContent: 'flex-end',
          textAlign: 'right',
          whiteSpace: 'nowrap'
        }}
      >
        {productsLoading ? <CircularProgress color="inherit" size={22} sx={{ mr: 1 }} /> : valueElement}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: `${theme.spacing(3)}`,
        minWidth: 0,
        // Resumo e CTA acompanham a rolagem (só a partir de md, onde há 2 colunas). O `top` soma a altura do topo
        // fixo da jornada (header + stepper, exposta por `StandardJourneyBrandedScreen` em `--journey-sticky-top`):
        // com só 24px o primeiro cartão ficava por baixo do stepper e o título «R$ X /mês» era cortado.
        position: { md: 'sticky', xs: 'static' },
        top: { md: `calc(var(${JOURNEY_STICKY_TOP_VAR}, 0px) + 24px)`, xs: 'auto' }
      }}
    >
      {headline ? <Box sx={{ color: 'primary.main', lineHeight: 1.25 }}>{headline}</Box> : null}

      <Card component="aside" sx={cardSx} variant="outlined">
        <Box alignItems="center" display="flex" gap={1} justifyContent="space-between" width="100%">
          {/* Valor e «/mês» numa linha só (nunca quebram): fonte reduzida para caber ao lado do selo. */}
          <Box
            alignItems="baseline"
            data-app-id="journey-quote-monthly-value"
            display="flex"
            flexWrap="nowrap"
            gap={0.5}
            minWidth={0}
            sx={{ whiteSpace: 'nowrap' }}
          >
            <Typography color="primary" sx={{ fontSize: SUMMARY_HERO_FONT_SIZE, fontWeight: 700 }} variant="h5">
              {fmt(totalMonthlyNumber)}
            </Typography>
            <Typography color="primary" sx={{ fontSize: SUMMARY_SUFFIX_FONT_SIZE, fontWeight: 600 }} variant="h6">
              /mês
            </Typography>
          </Box>
          <Box data-app-id="journey-quote-installments-badge" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Typography color="primary" sx={{ fontSize: SUMMARY_BADGE_FONT_SIZE, fontWeight: 600 }} variant="h6">
              por 12 meses
            </Typography>
          </Box>
        </Box>
        {subscriptionRows.map((line) =>
          row(
            line.description ? (
              <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
                {line.label}
                <ModalHelpClub clubDiscription={line.description} />
              </Box>
            ) : (
              line.label
            ),
            <Typography color="primary" sx={{ fontWeight: 600 }} variant="subtitle2">
              {fmtPrice(line.amount)}
            </Typography>,
            line.id
          )
        )}
        {showOperationalRow && serviceChargeFormatted
          ? row(
              'Custos operacionais',
              <Typography color="primary" sx={{ fontWeight: 600 }} variant="subtitle2">
                {serviceChargeFormatted}
              </Typography>,
              'op'
            )
          : null}
        <Divider flexItem sx={{ borderColor: 'divider', mt: 0.5, opacity: 0.85 }} />
        <Box display="flex" justifyContent="space-between" pb={1} pt={1} width="100%">
          <Typography color="primary" sx={{ fontWeight: 700 }} variant="h6">
            Total:
          </Typography>
          {productsLoading ? (
            <CircularProgress size={22} />
          ) : (
            <Typography color="primary" sx={{ fontWeight: 700, textAlign: 'right' }} variant="h6">
              {totalMonthlyFormatted}
            </Typography>
          )}
        </Box>
      </Card>

      {membershipFee > 0 ? (
        <Card component="aside" sx={cardSx} variant="outlined">
          <Box alignItems="center" display="flex" gap={1} justifyContent="space-between" width="100%">
            <Box
              alignItems="baseline"
              display="flex"
              flexWrap="nowrap"
              gap={0.5}
              minWidth={0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              <Typography color="primary" sx={{ fontSize: SUMMARY_HERO_FONT_SIZE, fontWeight: 700 }} variant="h5">
                {membershipFeeFormatted}
              </Typography>
              <Typography color="primary" sx={{ fontSize: SUMMARY_SUFFIX_FONT_SIZE, fontWeight: 600 }} variant="h6">
                /taxa única
              </Typography>
            </Box>
            {membershipPaid ? (
              <Box
                aria-label="Taxa de adesão paga"
                data-app-id="journey-quote-membership-paid"
                role="img"
                sx={{
                  alignItems: 'center',
                  bgcolor: 'success.main',
                  borderRadius: '100px',
                  color: 'success.contrastText',
                  display: 'flex',
                  flexShrink: 0,
                  height: 32,
                  justifyContent: 'center',
                  width: 32
                }}
              >
                <CheckIcon sx={{ fontSize: 18 }} />
              </Box>
            ) : null}
          </Box>
          {row(
            'Taxa de adesão',
            <Typography color="primary" sx={{ fontWeight: 600 }} variant="subtitle2">
              {membershipFeeFormatted}
            </Typography>,
            'membership-row'
          )}
          <Divider flexItem sx={{ borderColor: 'divider', mt: 0.5 }} />
          <Box display="flex" justifyContent="space-between" pb={1} pt={1} width="100%">
            <Typography color="primary" sx={{ fontWeight: 700 }} variant="h6">
              Total:
            </Typography>
            <Typography color="primary" sx={{ fontWeight: 700, textAlign: 'right' }} variant="h6">
              {membershipFeeFormatted}
            </Typography>
          </Box>
        </Card>
      ) : null}

      {termsSlot ? <Box data-app-id="journey-quote-terms">{termsSlot}</Box> : null}

      {showAdvanceButton ? (
        <LoadingButton
          aria-label={advanceLabel}
          data-app-id="journey-quote-advance-payment"
          disabled={disableAdvance}
          fullWidth
          loading={advancing}
          onClick={onAdvance}
          size="large"
          sx={{ borderRadius: `${radius.large}px` }}
          variant="contained"
        >
          {advanceLabel}
        </LoadingButton>
      ) : null}

      {onDownloadPdf ? (
        <Button
          data-app-id="journey-quote-download-pdf"
          fullWidth
          onClick={onDownloadPdf}
          size="large"
          sx={{ borderRadius: `${radius.large}px` }}
          variant="outlined"
        >
          Baixar cotação (PDF)
        </Button>
      ) : null}

      <Box
        alignItems="center"
        bgcolor="customColors.backgroundTertiary"
        borderRadius={`${radius.small}px`}
        display="flex"
        gap={2}
        p={3}
        sx={{
          border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.35) : 'transparent'}`
        }}
      >
        <LockOutlinedIcon sx={{ color: theme.palette.success.main, flexShrink: 0 }} fontSize="small" aria-hidden />
        <Typography sx={{ color: theme.customColors.tertiaryContrastText }} variant="body2">
          {securityMessage}
        </Typography>
      </Box>
    </Box>
  );
}
